<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import BaseVideoThumnail from '@/components/base/BaseVideoThumbnail.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { ApiCursorBody } from '@/types/api-response.types'
import type { FilterOption } from '@/types/filter.types'
import type { Lecture } from '@/types/lecture.types'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{
  selectedProgram?: FilterOption
  selectedClass?: FilterOption
  filters?: {
    level?: string
    sort?: string
  }
}>()

const isMobile = useIsMobile()
const router = useRouter()

const computedFilters = computed(() => {
  if (props.filters?.level === 'all') {
    return {
      level: undefined,
      ascending: props.filters.sort === 'oldest',
    }
  }

  return {
    level: props.filters?.level,
    ascending: props.filters?.sort === 'oldest',
  }
})

const computedCourseIdx = computed(() => {
  if (props.selectedClass?.idx === 0) {
    return undefined
  }

  return props.selectedClass?.idx
})

// 스크롤 감지를 위한 변수들
const isLoading = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

// 비디오 리스트 데이터
const lectureCursorInfo = ref<ApiCursorBody<Lecture>>()
const videoList = ref<Lecture[]>([])

const hoveredVideoId = ref<number | null>(null)

const handleMouseEnter = (videoId: number) => {
  if (isMobile.value) return
  hoveredVideoId.value = videoId
}

const handleMouseLeave = () => {
  if (isMobile.value) return
  hoveredVideoId.value = null
}

const handleVideoClick = (videoId: number) => {
  router.push(`/video/${videoId}`)
}

// 추가 비디오 로드 함수
const loadMoreVideos = async () => {
  if (isLoading.value || !lectureCursorInfo.value?.hasNext) return

  isLoading.value = true
  try {
    const nextCursor = lectureCursorInfo.value?.nextCursor
    const lectureCursorList = await lectureService.getLectures({
      cursor: nextCursor,
      size: 18,
      courseIdx: computedCourseIdx.value,
      programIdx: props.selectedProgram?.idx,
      ...computedFilters.value,
    })

    videoList.value = [...videoList.value, ...lectureCursorList.data]
    lectureCursorInfo.value = lectureCursorList
  } catch (error) {
    console.error('비디오 로드 중 오류 발생:', error)
  } finally {
    isLoading.value = false
  }
}

// Intersection Observer 설정
const setupIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMoreVideos()
      }
    },
    { threshold: 0.1 },
  )

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value)
  }
}

watch(
  () => [props.selectedClass, props.filters],
  async () => {
    const lectureCursorList = await lectureService.getLectures({
      cursor: undefined,
      size: 18,
      courseIdx: computedCourseIdx.value,
      programIdx: props.selectedProgram?.idx,
      ...computedFilters.value,
    })
    videoList.value = lectureCursorList.data
    lectureCursorInfo.value = lectureCursorList
  },
  { deep: true },
)

onMounted(async () => {
  setupIntersectionObserver()
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<template>
  <div class="video-list-section">
    <BaseVideoThumnail
      class="video-list-section-item"
      v-for="video in videoList"
      :key="video.idx"
      :lecture="video"
      :show-level="isMobile || hoveredVideoId === video.idx"
      :is-free="video.free"
      @mouseenter="handleMouseEnter(video.idx)"
      @mouseleave="handleMouseLeave"
      @video-click="handleVideoClick(video.idx)"
    />

    <!-- 결과가 없는 경우 -->
    <div v-if="videoList.length === 0" class="empty-state">
      <p class="body-md gray-600">해당 조건에 맞는 결과가 없습니다.</p>
    </div>

    <!-- 로딩 트리거 요소 -->
    <div ref="loadMoreTrigger" class="load-more-trigger">
      <div v-if="isLoading" class="loading-spinner">로딩 중...</div>
    </div>
  </div>
</template>

<style scoped>
.video-list-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 28px;
}

.video-list-section-item {
  transition: transform 0.2s ease;
}

.video-list-section-item:hover {
  transform: scale(1.24);
  z-index: 100;
  border: 1px solid var(--tint-300);
  cursor: pointer;
}

.video-list-section:has(.video-list-section-item:hover) .video-list-section-item:not(:hover) {
  opacity: 0.2;
  transition: opacity 0.1s ease;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
}

.load-more-trigger {
  grid-column: 1 / -1;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-spinner {
  color: var(--gray-600);
}

@media (max-width: 1365px) {
  .video-list-section {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 8px;
  }

  .video-list-section-item:hover {
    transform: none;
    border: none;
  }

  .video-list-section:has(.video-list-section-item:hover) .video-list-section-item:not(:hover) {
    opacity: 1;
    transition: none;
  }
}
</style>
